import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.png';
import byLawsPdf from '../../assets/pdfs/Victorias-Place-By-Laws.pdf';
import { useSiteAuth } from '../hooks/useSiteAuth';

const primaryNavLinks = [{ path: '/', label: 'Home' }];

const aboutMenuLinks = [{ path: '/board-members', label: 'Board Bios' }];

const aboutDocumentLinks = [{ href: byLawsPdf, label: 'By-Laws' }];

const primaryUtilityLinks = [
  { path: '/programs', label: 'Programs' },
  { path: '/events', label: 'Events' },
];

const secondaryNavLinks = [
  { path: '/gallery', label: 'Gallery' },
  { path: '/resources', label: 'Resources' },
  { path: '/contact', label: 'Contact' },
];

export default function Header() {
  const { pathname } = useLocation();
  const { isAuthenticated, isInitializing } = useSiteAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const showPortalLink = isAuthenticated && !isInitializing;
  const isAboutSectionActive = pathname === '/about-us' || pathname === '/board-members';
  const authLink = {
    path: showPortalLink ? '/portal' : '/sign-in',
    label: showPortalLink ? 'Portal' : 'Sign in',
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsMobileMenuOpen(false);
      }
    };

    if (mediaQuery.matches) {
      setIsMobileMenuOpen(false);
    }

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return (
    <header className="site-header">
      <div className="site-header-support">
        <p>Faith-rooted support for children with Down Syndrome and the families who love them.</p>
      </div>

      <div className="site-header-shell">
        <nav className="site-header-nav" aria-label="Main navigation">
          <div className="site-header-brand-wrap">
            <Link to="/" className="site-header-brand" aria-label="Victoria's Place home">
              <span className="header-brand-mark">
                <img src={logo} alt="" className="header-brand-logo" />
              </span>

              <span className="site-header-brand-copy">
                <span className="site-header-brand-name">Victoria&apos;s Place</span>
                <span className="site-header-brand-tag">Faith. Family. Community</span>
              </span>
            </Link>
          </div>

          <button
            type="button"
            className={`site-header-menu-toggle ${isMobileMenuOpen ? 'site-header-menu-toggle--open' : ''}`}
            aria-expanded={isMobileMenuOpen}
            aria-controls="site-mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setIsMobileMenuOpen((current) => !current)}
          >
            <span className="site-header-menu-toggle-label">Menu</span>
            <span className="site-header-menu-toggle-icon" aria-hidden="true">
              <span className="site-header-menu-toggle-bar" />
              <span className="site-header-menu-toggle-bar" />
              <span className="site-header-menu-toggle-bar" />
            </span>
          </button>

          <div className="site-header-center">
            <div className="header-nav-list">
              <div className="header-nav-group">
                {primaryNavLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `header-nav-link ${isActive ? 'header-nav-link--active' : ''}`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}

                <div className={`header-nav-dropdown ${isAboutSectionActive ? 'header-nav-dropdown--active' : ''}`}>
                  <NavLink
                    to="/about-us"
                    className={({ isActive }) =>
                      `header-nav-link header-nav-link--dropdown ${
                        isActive || isAboutSectionActive ? 'header-nav-link--active' : ''
                      }`
                    }
                  >
                    <span>About Us</span>
                    <span className="header-nav-dropdown-caret" aria-hidden="true" />
                  </NavLink>

                  <div className="header-nav-dropdown-menu">
                    {aboutMenuLinks.map((link) => (
                      <NavLink
                        key={link.path}
                        to={link.path}
                        className={({ isActive }) =>
                          `header-dropdown-link ${isActive ? 'header-dropdown-link--active' : ''}`
                        }
                      >
                        {link.label}
                      </NavLink>
                    ))}

                    {aboutDocumentLinks.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        className="header-dropdown-link"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>

                {primaryUtilityLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `header-nav-link ${isActive ? 'header-nav-link--active' : ''}`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>

              <span className="header-nav-divider" aria-hidden="true" />

              <div className="header-nav-group">
                {secondaryNavLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `header-nav-link ${isActive ? 'header-nav-link--active' : ''}`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          <div className="site-header-actions">
            <Link to={authLink.path} className="header-auth-link">
              {authLink.label}
            </Link>
            <Link to="/give" className="header-action-button header-action-button--primary">
              Donate
            </Link>
            <Link to="/contact" className="header-action-button header-action-button--secondary">
              Volunteer
            </Link>
          </div>

          <AnimatePresence initial={false}>
            {isMobileMenuOpen ? (
              <motion.div
                id="site-mobile-menu"
                className="site-mobile-menu"
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <div className="site-mobile-menu-links">
                  {primaryNavLinks.map((link) => (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `header-nav-link header-nav-link--mobile ${
                          isActive ? 'header-nav-link--active' : ''
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  ))}

                  <NavLink
                    to="/about-us"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `header-nav-link header-nav-link--mobile ${
                        isActive || isAboutSectionActive ? 'header-nav-link--active' : ''
                      }`
                    }
                  >
                    About Us
                  </NavLink>

                  <div className="site-mobile-menu-subgroup">
                    {aboutMenuLinks.map((link) => (
                      <NavLink
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `header-nav-link header-nav-link--mobile header-nav-link--mobile-child ${
                            isActive ? 'header-nav-link--active' : ''
                          }`
                        }
                      >
                        {link.label}
                      </NavLink>
                    ))}

                    {aboutDocumentLinks.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        className="header-nav-link header-nav-link--mobile header-nav-link--mobile-child"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>

                  {primaryUtilityLinks.map((link) => (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `header-nav-link header-nav-link--mobile ${
                          isActive ? 'header-nav-link--active' : ''
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  ))}

                  {secondaryNavLinks.map((link) => (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `header-nav-link header-nav-link--mobile ${
                          isActive ? 'header-nav-link--active' : ''
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </div>

                <div className="site-mobile-menu-actions">
                  <Link
                    to={authLink.path}
                    className="header-auth-link header-auth-link--mobile"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {authLink.label}
                  </Link>
                  <Link
                    to="/give"
                    className="header-action-button header-action-button--primary header-action-button--mobile"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Donate
                  </Link>
                  <Link
                    to="/contact"
                    className="header-action-button header-action-button--secondary header-action-button--mobile"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Volunteer
                  </Link>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </nav>
      </div>
    </header>
  );
}
